import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  Signal,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import * as Sentry from '@sentry/capacitor';
import { LocalizedDateFormatPipe } from '@wilson/localization';
import { MinutesToHHmmDurationPipe } from '@wilson/pipes';
import {
  endOfMonth,
  endOfWeek,
  getMonth,
  getWeek,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzTypographyModule } from 'ng-zorro-antd/typography';

export enum KpiMode {
  Week = 'week',
  Month = 'month',
}

@Component({
  selector: 'wilson-kpi-value',
  imports: [
    CommonModule,
    MinutesToHHmmDurationPipe,
    NzTooltipModule,
    NzTypographyModule,
    TranslateModule,
    NzSkeletonModule,
  ],
  templateUrl: './kpi-value.component.html',
  styleUrls: ['./kpi-value.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KpiValueComponent {
  private readonly localizedDateFormat = inject(LocalizedDateFormatPipe);

  @Input() kpiName!: string;
  @Input() kpiValueInMinutes: number | null | undefined;
  @Input() isLoadingKpiValue!: boolean;
  @Input() kpiMode: KpiMode = KpiMode.Week;
  @Input() set currentTimelineStartDate(value: string | null) {
    if (value) {
      try {
        const date = new Date(value);
        if (this.kpiMode === KpiMode.Week) {
          const weekStart = startOfWeek(date, { weekStartsOn: 1 });
          const weekEnd = endOfWeek(date, { weekStartsOn: 1 });
          this.startDate =
            this.localizedDateFormat.toFormattedSignal(weekStart);
          this.endDate = this.localizedDateFormat.toFormattedSignal(weekEnd);
          this.weekNumber = getWeek(date, { weekStartsOn: 1 });
        } else if (this.kpiMode === KpiMode.Month) {
          const monthStart = startOfMonth(date);
          const monthEnd = endOfMonth(date);
          this.startDate =
            this.localizedDateFormat.toFormattedSignal(monthStart);
          this.endDate = this.localizedDateFormat.toFormattedSignal(monthEnd);
          this.monthIndex = getMonth(date);
        }
      } catch (error) {
        console.error(error);
        Sentry.captureException(error, {
          level: 'error',
          tags: {
            area: 'wilson-kpi-value: currentTimelineStartDate',
          },
        });
      }
    }
  }

  startDate!: Signal<string>;
  endDate!: Signal<string>;
  monthIndex!: number;
  weekNumber!: number;
  KpiMode = KpiMode;
}
